import { ArrowLeft01Icon } from '@hugeicons-pro/core-stroke-rounded';
import type { NativeStackHeaderProps, NativeStackNavigationOptions } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Pressable, Platform, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Box } from '../gluestack/box';
import { HStack } from '../gluestack/hstack';
import { Text } from '../gluestack/text';
import { VStack } from '../gluestack/vstack';

import { ConnectionBanner } from '@/components/ui/ConnectionBanner';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { ImagePattern } from '@/components/ui/ImagePattern';
import { UpdateBanner } from '@/components/ui/UpdateBanner';
import { useTheme } from '~/components/providers/ThemeProvider';
import { useVersionCheck } from '~/components/providers/VersionCheckProvider';
import { SafeBlurView as BlurView } from '~/components/ui/SafeBlurView';

// Define custom options type extending NativeStackNavigationOptions
interface CustomNativeStackNavigationOptions extends NativeStackNavigationOptions {
  headerBottom?: () => React.ReactNode;
  hideTitle?: boolean;
}

export function MobileHeader(props: NativeStackHeaderProps) {
  const { navigation, route, back } = props;
  // Cast options to the custom type to access headerBottom
  const options = props.options as CustomNativeStackNavigationOptions;

  const { t } = useTranslation();
  const { newVersionAvailable, reloadPage } = useVersionCheck();
  const { getThemeColor, currentPattern } = useTheme();
  const secondaryColor = getThemeColor('secondary', '500');
  const insets = useSafeAreaInsets();
  const { fontScale } = useWindowDimensions();
  const top = insets.top + (Platform.OS === 'web' ? 20 : 8);
  const contentHeight = Platform.OS === 'android' ? Math.ceil(38 * fontScale) : 38;
  const headerHeight = contentHeight + top;

  const showBackButton = !!back;

  return (
    <VStack>
      <VStack
        className={`overflow-hidden ${options.headerTransparent ? 'bg-transparent' : 'bg-secondary-500'}`}
        style={{ height: headerHeight }}>
        {!options.headerTransparent && (
          <ImagePattern
            backgroundColor={secondaryColor}
            scale={currentPattern.scale}
            source={currentPattern.source}
          />
        )}
        <HStack className="absolute inset-0 m-4 mt-0 items-center" style={{ top }}>
          {/* Left Section - Absolutely positioned to the left */}
          <HStack className="absolute left-0 z-10 items-center">
            {showBackButton &&
              (options.headerTransparent ? (
                <Box className="mr-3 overflow-hidden rounded-full">
                  <BlurView intensity={80} tint="dark" className="p-1">
                    <Pressable
                      onPress={() => navigation.goBack()}
                      hitSlop={10}
                      accessibilityLabel={t('common.back')}>
                      <HugeIconsWrapper icon={ArrowLeft01Icon} size={28} className="color-white" />
                    </Pressable>
                  </BlurView>
                </Box>
              ) : (
                <Pressable
                  onPress={() => navigation.goBack()}
                  className="mr-3"
                  hitSlop={10}
                  accessibilityLabel={t('common.back')}>
                  <HugeIconsWrapper icon={ArrowLeft01Icon} size={28} className="color-white" />
                </Pressable>
              ))}
            {options.headerLeft && options.headerLeft({ tintColor: 'white' })}
          </HStack>

          {/* Center fills width between sides; lower z-index so headerLeft/headerRight stay on top */}
          <HStack className="absolute inset-x-0 bottom-0 top-0 z-0 items-center justify-center">
            {options.headerTitle && typeof options.headerTitle === 'function'
              ? options.headerTitle({ children: options.title || route.name, tintColor: 'white' })
              : !options.hideTitle && (
                  <Text size="lg" bold className="text-white">
                    {options.title || route.name}
                  </Text>
                )}
          </HStack>

          {/* Right Section - Absolutely positioned to the right */}
          <HStack className="absolute right-0 z-10 items-center">
            {options.headerRight && options.headerRight({ tintColor: 'white' })}
          </HStack>
        </HStack>
      </VStack>
      {newVersionAvailable && <UpdateBanner onReload={reloadPage} />}
      <ConnectionBanner />
      {/* Bottom Section */}
      {options.headerBottom && options.headerBottom()}
    </VStack>
  );
}
